import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, StatusChip, UserNameCell } from '@/components/core';
import { SickLeaveRequest } from '../../types/sick-leave.types';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';

const SICK_LEAVE_STATUSES = ['Pending', 'Approved', 'Rejected', 'Withdrawn', 'Cancelled'];

interface CreateSickLeaveTableColumnsProps {
  t: (key: string) => string;
  /** The employee's email by IAM user id, shown under the name. */
  emailOf?: (userId: string) => string | undefined;
}

export const createSickLeaveTableColumns = ({
  t,
  emailOf,
}: CreateSickLeaveTableColumnsProps): ColumnDef<SickLeaveRequest>[] => [
  {
    id: 'DisplayName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.DisplayName ?? '',
    cell: ({ row }) => (
      <UserNameCell name={row.original.DisplayName} email={emailOf?.(row.original.UserId)} />
    ),
  },
  {
    id: 'StartDate',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('START_DATE')} />,
    accessorFn: (row) => row.StartDate,
    cell: ({ row }) => formatDateOnly(row.original.StartDate),
  },
  {
    id: 'EndDate',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('END_DATE')} />,
    accessorFn: (row) => row.EndDate,
    cell: ({ row }) => formatDateOnly(row.original.EndDate),
  },
  {
    id: 'Reason',
    // Card: in the footer, as in the old portal.
    meta: { filter: { type: 'text' }, mobile: 'footer' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('REASON')} />,
    accessorFn: (row) => row.Reason?.trim() || '-',
  },
  {
    id: 'Status',
    meta: {
      filter: {
        type: 'options',
        options: SICK_LEAVE_STATUSES.map((status) => ({
          value: status,
          label: t(status.toUpperCase()),
        })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.Status,
    cell: ({ row }) => (
      <StatusChip status={row.original.Status}>{t(row.original.Status.toUpperCase())}</StatusChip>
    ),
  },
];
